import { GetterTree } from 'vuex';

import ICheckoutState from '@/interfaces/CheckoutState';

import { EPaymentMethod, EEligibilityAction, EPaymentMethodTypes } from '@/helpers/enums';
import { EBankSlipInstallmentSteps } from '@/shared/enums/EBankSlipInstallmentSteps';

import { intervalBuild } from '@/helpers/checkout';

import i18n from '@/plugins/i18n';
import {
  ICheckoutCartOffer,
  ICheckoutCartOfferAvailability,
  ICheckoutCartOfferEligibility,
  ICheckoutCartOfferCoupon,
  IInvoice,
  ICheckoutCartOfferPayment,
  ICheckoutCartOfferPrice,
  ICheckoutCartOfferDiscount
} from '@/interfaces/Checkout';
import { IPayment } from '@/interfaces/Payment';

import { Offer } from '@/classes/Checkout/Offer';

export default {
  selectedOffer(state: ICheckoutState): ICheckoutCartOffer {
    const rootOffer = state.checkout.cart.offer;
    const relatedOffers = rootOffer.related_offers as ICheckoutCartOffer[];

    if (rootOffer.is_selected) {
      return rootOffer;
    }

    const relatedSelected = relatedOffers && relatedOffers.find(relatedOffer => Boolean(relatedOffer.is_selected));

    return relatedSelected ? relatedSelected : rootOffer;
  },
  selectedOfferIsRelated(state: ICheckoutState, getters): boolean {
    return getters.selectedOffer.id !== getters.rootOffer.id;
  },
  selectedOfferAvailability(state: ICheckoutState, getters): ICheckoutCartOfferAvailability {
    return getters.selectedOffer.availability;
  },
  selectedOfferEligibility(state: ICheckoutState, getters): ICheckoutCartOfferEligibility {
    return getters.selectedOffer.eligibility;
  },
  selectedOfferPayment(state: ICheckoutState, getters): IPayment {
    return getters.selectedOffer.payment;
  },
  selectedOfferInvoice(state: ICheckoutState, getters): IInvoice {
    return getters.selectedOffer.invoice;
  },
  selectedOfferCoupon(_: ICheckoutState, getters): ICheckoutCartOfferCoupon {
    const { coupon } = getters.selectedOffer;

    if (coupon && coupon.discount_id) {
      const { discounts = [] } = getters.selectedOfferPaymentStructure.discounts || {};

      const discount = (discounts as ICheckoutCartOfferDiscount[]).find(discount => coupon.discount_id === discount.id);

      if (discount) {
        coupon.amount = discount.applied_amount;
      }
    }

    return coupon;
  },
  selectedOfferRelated(state: ICheckoutState, getters): ICheckoutCartOffer[] {
    return Offer.related(getters.selectedOffer);
  },
  rootOffer(state: ICheckoutState): ICheckoutCartOffer {
    return state.checkout.cart.offer;
  },
  rootOfferRelated(state: ICheckoutState): ICheckoutCartOffer[] {
    return Offer.related(state.checkout.cart.offer);
  },
  rootOfferPayment(state: ICheckoutState, getters): IPayment {
    return state.checkout.cart.offer.payment;
  },
  rootOfferInvoice(state: ICheckoutState, getters): IInvoice {
    return state.checkout.cart.offer.invoice;
  },
  isRenewal(state: ICheckoutState, getters) {
    return getters.selectedOfferEligibility
      && [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal]
        .includes(getters.selectedOfferEligibility.eligible_action);
  },
  renewalContractId(state: ICheckoutState, getters) {
    return getters.selectedOfferEligibility
      && [EEligibilityAction.earlyRenewal, EEligibilityAction.lateRenewal]
        .includes(getters.selectedOfferEligibility.eligible_action) && getters.selectedOfferEligibility.early_renewal_contract;
  },
  renewalMessage(state: ICheckoutState, getters) {
    const offer = getters.selectedOffer;

    if (offer) {
      const str = String(i18n.t('App.Checkout.Renew')).replace(
        '{interval}',
        intervalBuild(offer.interval_type, offer.interval_count)
      );

      return `<b>${i18n.t('App.attention')}</b> ${str}`;
    }

    return '';
  },
  selectedOfferPaymentCardStep(state: ICheckoutState, getters) {
    return getters.selectedOfferPayment.getPaymentMethod(EPaymentMethod.creditCard).step;
  },
  selectedOfferPaymentCardSelected(state: ICheckoutState, getters) {
    return getters.selectedOfferPayment.getPaymentMethod(EPaymentMethod.creditCard).card;
  },
  selectedOfferSelectedPaymentMethod(state: ICheckoutState, getters) {
    return getters.selectedOfferPayment.selectedPaymentMethod;
  },
  isAddressRequired(state: ICheckoutState, getters) {
    const offer = getters.selectedOffer;

    return offer && offer.is_address_required;
  },
  selectedOfferTestimonials(state: ICheckoutState, getters) {
    return getters.selectedOffer.offer_testimonials;
  },
  selectedOfferPrice(state: ICheckoutState, getters) {
    return getters.selectedOffer.offer_price.find((offerPrice: ICheckoutCartOfferPrice) => {
      return offerPrice.isSelected;
    });
  },
  selectedOfferPaymentStructure(state: ICheckoutState, getters) {
    return getters.selectedOffer.payment_structure.find((paymentStructure: ICheckoutCartOfferPayment) => {
      return paymentStructure.price_id === getters.selectedOfferPrice.id;
    });
  },
  rewardsPointsPaymentIsAvailable(state: ICheckoutState, getters) {
    return getters.selectedOfferPayment.paymentTypeIsAvailable(EPaymentMethodTypes.empiricusRewards);
  },
  order: (state: ICheckoutState) => state.order,
  customTemplateGuid: (state: ICheckoutState) => state.checkout.cart.custom_template_guid,
  cartUuid: (state: ICheckoutState) => state.checkout.cart.uuid,
  suggestedOffer(state: ICheckoutState): ICheckoutCartOffer | null {
    return state.checkout.cart.suggested_offer;
  },
  selectedOfferDiscountByPaymentMethod(state: ICheckoutState, getters) {
    return (method: EPaymentMethod) => getters.selectedOfferPayment.getPaymentMethod(method).discount_percentage;
  },
  isKoinActivated(state: ICheckoutState) {
    return state.koin.activated;
  },
  bankSlipAcceptedTermsChecked(state: ICheckoutState) {
    return state.koin.checked;
  },
  bankSlipStep(state: ICheckoutState) {
    return state.koin.step;
  },
  disablePurchaseKoin(state: ICheckoutState, getters) {
    return !state.koin.activated || !!(state.koin.checked && state.koin.installment) || getters.disableBankSlipInstallments;
  },
  disableBankSlipInstallments(state: ICheckoutState) {
    return state.koin.checked && state.koin.step === EBankSlipInstallmentSteps.initial;
  },
  bankSlipInstallmentSelected(state: ICheckoutState) {
    return state.koin.installment;
  },
  selectedFilterCreditCard(state: ICheckoutState) {
    return state.creditCard.filterActived;
  },
  selectedRefCreditCardFormActived(state: ICheckoutState) {
    return state.creditCard.refFormActived;
  },
  getOfferPaymentConditionIdByMethodCode(state: ICheckoutState, getters) {
    return (method: EPaymentMethod) => {
      const paymentStructuresFiltered = getters.getOfferPaymentStructure;
      const paymentCondition = paymentStructuresFiltered.find((value: any) => {
        return value.payment_conditions[0].payment_method_code === method && value.payment_conditions[0].main_payment_condition;
      });

      return paymentCondition && paymentCondition.offer_payment_condition_id;
    };
  },
  getOfferPaymentStructure(state: ICheckoutState, getters) {
    return getters.selectedOffer.payment_structure.filter((payment_structure_value: any) => {
      return payment_structure_value.is_multi_payment_options === false && payment_structure_value.payment_conditions && payment_structure_value.payment_conditions.length === 1;
    });
  },
  getOfferPaymentConditionByMethodCode(state: ICheckoutState, getters) {
    return (method: EPaymentMethod) => {
      const paymentStructuresFiltered = getters.getOfferPaymentStructure;
      const paymentCondition = paymentStructuresFiltered.find((value: any) => {
        return value.payment_conditions[0].payment_method_code === method && value.payment_conditions[0].main_payment_condition;
      });

      return paymentCondition;
    };
  },
} as GetterTree<ICheckoutState, any>;
